import { useQueries, useQuery } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";
import { rpcClient } from "@/lib/rpc";
import { EVIDENCE_DIM_LABEL, SKILL_SLUG_LABEL } from "./correction-target";

/**
 * Passive prompt-health banner shown above the calibration runs section.
 * Stays invisible while every `scoring:dim:<dim>` rubric is healthy;
 * surfaces a red callout when one is missing or malformed because that
 * single condition makes every fixture in the affected dim score 1.00 —
 * the production failure mode this banner exists to flag BEFORE the CHRO
 * triggers a calibration.
 *
 * Query reuses the same cache key as `PromptHealthSection` so opening
 * the engineering tile or re-mounting this banner doesn't double-fetch.
 */

interface PromptHealthRow {
  section: string;
  dim: string;
  present: boolean;
  okShape: boolean;
  bytes: number;
  updatedAt: string | null;
}

interface PromptHealthResponse {
  rows: PromptHealthRow[];
}

interface AgentJobProgress {
  warnings?: ("missing_rubric" | string)[];
}

interface InFlightItem {
  correctionId: string;
  identity?: { evidenceId: string } | null;
}

// biome-ignore lint/security/noSecrets: query key, not a secret
const QK = ["labs", "calibrationRunsPromptHealth"];

function dimLabel(id: string): string {
  return EVIDENCE_DIM_LABEL[id] ?? SKILL_SLUG_LABEL[id] ?? id;
}

export function PromptHealthBanner({
  liveItems = [],
}: {
  /** Active in-flight corrections — when any of their progress endpoints
   *  surfaces a `missing_rubric` warning, we promote it from a per-tile
   *  warning to this top-level banner so the operator can't miss it. */
  liveItems?: InFlightItem[];
}) {
  const { data } = useQuery<PromptHealthResponse>({
    queryKey: QK,
    queryFn: () => rpcClient.labs.calibrationRunsPromptHealth(),
    // 60s is plenty — the rubric set rarely changes intra-session, and a
    // shorter interval would just hammer the DB.
    refetchInterval: 60_000,
  });

  // Side-channel: if any live correction's progress carries a
  // `missing_rubric` warning, surface that here so the operator gets one
  // banner instead of N per-tile warnings buried inside expanded rows.
  const liveProgress = useQueries({
    queries: liveItems.map((it) => ({
      queryKey: ["labs", "correctionJobsGetProgress", it.correctionId],
      queryFn: async (): Promise<AgentJobProgress | null> => {
        try {
          return await rpcClient.labs.correctionJobsGetProgress({ jobId: it.correctionId });
        } catch {
          return null;
        }
      },
      refetchInterval: 2000,
    })),
  });
  const liveAffectedDims = new Set<string>();
  for (let i = 0; i < liveItems.length; i += 1) {
    const p = liveProgress[i].data;
    if (p?.warnings?.includes("missing_rubric")) {
      const dim = liveItems[i].identity?.evidenceId;
      if (dim) liveAffectedDims.add(dim);
    }
  }

  const stored = data && Array.isArray(data.rows) ? data.rows.filter((r) => !r.okShape) : [];
  // Union the two sources — the prompt-health table catches stored
  // breakage; the live signal catches transient gaps the agent hit on the
  // current run (in case a hot reseed flipped state mid-run).
  const allDims = new Set<string>();
  for (const r of stored) allDims.add(r.dim);
  for (const d of Array.from(liveAffectedDims)) allDims.add(d);

  if (allDims.size === 0) return null;
  const dims = Array.from(allDims);

  return (
    <div className="prompt-health-banner" role="alert" data-testid="prompt-health-banner">
      <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
      <div>
        <strong className="prompt-health-banner-title">
          {dims.length === 1
            ? `Rubrica ausente: ${dimLabel(dims[0])}`
            : `${dims.length} rubricas ausentes ou malformadas`}
        </strong>
        <span className="prompt-health-banner-body">
          {" "}
          Toda nota dessas dimensões vai sair <strong>1.00</strong>. Antes de rodar a calibração,
          corrija em <em>Modo completo → Engenharia → Saúde dos prompts</em> ou peça ao time técnico
          para reseed do disco.
        </span>
      </div>
    </div>
  );
}
